<template>
  <div class="home-view">
    <GearAtmosphere />
    <div class="page-rack" aria-hidden="true">
      <div class="page-rack-teeth">
        <span v-for="tooth in 160" :key="tooth" class="page-rack-tooth"></span>
      </div>
    </div>
    <main class="home-content">
    <section class="hero section" :style="{ '--hero-watermark': `url('${brand.logo}')` }">
      <div class="shell hero-inner">
        <router-link class="demo-log-callout" to="/managed-maintenance-explained">
          <span>Try out the demo maintenance logs</span>
          <span aria-hidden="true">→</span>
        </router-link>

        <div class="hero-grid">
          <div class="hero-copy-column">
            <h1>{{ home.hero.title }}</h1>
            <p class="hero-copy">{{ home.hero.description }}</p>
            <router-link class="btn btn-primary" to="/services-request">{{ home.hero.cta }}</router-link>
          </div>
          <div class="hero-side">
            <!-- The same estimator link that closes the CFM section, lifted into
                 the hero. Down there it lands 72% of the way down the page,
                 behind four paragraphs — far enough that it reads as absent. -->
            <router-link class="btn btn-primary hero-estimate" to="/maintenance-estimate">
              Estimate Managed Maintenance for my machines
            </router-link>

            <nav class="hero-service-links" aria-label="Service options">
              <router-link class="hero-service-link" to="/services/managed-maintenance-systems">Managed Maintenance Plans</router-link>
              <router-link class="hero-service-link" to="/services/on-site-repairs">Right-Away Callout Repair</router-link>
              <router-link class="hero-service-link" to="/services/on-site-repairs">Scheduled Repair</router-link>
              <router-link class="hero-service-link" to="/services/additional-capabilities">Other Field Support</router-link>
            </nav>
          </div>
        </div>
      </div>
    </section>

    <section class="section">
      <div class="shell">
        <header class="page-head compact">
          <p class="eyebrow">Services</p>
          <h2 class="section-title">Mobile service built for uptime</h2>
          <p>Professional field support for equipment, fleet, and vehicle repair with a practical, downtime-first approach.</p>
        </header>

        <div class="service-grid">
          <router-link
            v-for="service in services"
            :key="service.slug"
            class="card service-card"
            :to="`/services/${service.slug}`"
            :aria-label="`Learn more about ${service.title}`"
          >
            <img :src="service.icon" :alt="service.title" />
            <h3>{{ service.title }}</h3>
            <p>{{ service.short }}</p>
          </router-link>
        </div>
      </div>
    </section>

    <PhotoAperture
      image="/assets/cdn/63d57d07be98890a95c78a1c/63dbfebd06f90d325c47c54e_am2.jpg"
      video="/assets/local/centerfire-aperture-field-service.mp4"
      alt="Centerfire mobile service truck working beside a customer vehicle"
    />

    <section class="section split">
      <div class="shell split-grid">
        <div>
          <p class="eyebrow">Why us?</p>
          <h2>{{ home.whyUs.title }}</h2>
          <p v-for="para in home.whyUs.paragraphs" :key="para">{{ para }}</p>
          <div class="split-actions">
            <router-link class="btn btn-primary" to="/maintenance-estimate">
              Estimate Managed Maintenance for my machines
            </router-link>
            <router-link class="btn btn-ghost" to="/service-options">Which service do I need?</router-link>
          </div>
        </div>
        <img :src="home.whyUs.image" alt="Centerfire service truck" class="why-image" />
      </div>
    </section>

    <div class="shell">
      <hr class="section-divider" />
    </div>

    <section class="section faq">
      <div class="shell faq-wrap">
        <div class="faq-content">
          <p class="eyebrow">Support</p>
          <h2>Frequently Asked Questions</h2>
          <p class="faq-lead">
            Customer satisfaction is our standard. These answers cover the most common scheduling, service area, and process questions.
          </p>
          <img
            class="faq-image"
            src="/assets/cdn/63d57d07be98892211c78a10/63dd46802632e636a6b8feaf_jb.webp"
            alt="Centerfire Services welding work"
          />
        </div>

        <div class="accordion-wrap">
          <article v-for="(item, index) in faq" :key="item.q" class="accordion-item" :class="{ open: openFaq === index }">
            <button
              class="accordion-toggle"
              type="button"
              :aria-expanded="openFaq === index ? 'true' : 'false'"
              :aria-controls="`faq-panel-${index}`"
              @click="openFaq = openFaq === index ? -1 : index"
            >
              <h3 class="accordion-title">{{ item.q }}</h3>
              <span class="accordion-icon" :class="{ open: openFaq === index }">⌄</span>
            </button>
            <div :id="`faq-panel-${index}`" class="accordion-list" :class="{ open: openFaq === index }">
              <p class="accordion-list-content">{{ item.a }}</p>
            </div>
          </article>
        </div>
      </div>
    </section>
    </main>
  </div>
</template>

<script>
import GearAtmosphere from '@/components/GearAtmosphere.vue';
import PhotoAperture from '@/components/PhotoAperture.vue';
import { brand, home, services, faq } from '@/data/content';

export default {
  components: {
    GearAtmosphere,
    PhotoAperture,
  },
  data() {
    return { brand, home, services, faq, openFaq: -1 };
  },
};
</script>

<style scoped>
.home-view {
  position: relative;
  isolation: isolate;
  background: var(--black);
}

/* One stacking lift on the container rather than on everything inside it. The
   rack is fixed at z-index 1 so it reads over the page's black ground; this
   puts the whole content layer above it in a single move.

   What stood here before did it per element — position: relative and z-index 2
   on every h1, h2, h3, p, a, button and img in the page. Because :is() takes
   the specificity of its most specific argument, that rule scored (0,1,1) and
   silently outranked any later single-class rule trying to position one of
   those elements. It quietly killed a sticky image before anyone caught it.
   Anything added here can now position itself normally. */
.home-content {
  position: relative;
  z-index: 2;
  overflow: clip;
}

/* This middle layer sits above section and card surfaces but below readable
   content. The brighter header copy of the rack remains independent. */
.page-rack {
  --brand-w: clamp(124px, 18.1vw, 160px);
  --logo-inset: calc(var(--brand-w) * 0.243 + 8px);
  position: fixed;
  inset: 0 auto 0 var(--logo-inset);
  z-index: 1;
  width: calc(var(--brand-w) * 0.15);
  overflow: hidden;
  pointer-events: none;
  opacity: 0.22;
  filter: drop-shadow(0 0 10px rgba(255, 90, 2, 0.08));
}

.page-rack::before {
  content: '';
  position: absolute;
  inset: 0 auto 0 0;
  width: calc(var(--brand-w) * 0.10625);
  background: var(--orange);
}

.page-rack-teeth {
  position: absolute;
  left: calc(var(--brand-w) * 0.01875);
  top: calc(var(--brand-w) * -0.5145 + 8px);
  width: calc(var(--brand-w) * 0.13125);
  display: flex;
  flex-direction: column;
  gap: calc(var(--brand-w) * 0.0625);
  transform: translate3d(0, var(--rack-travel-y, 0px), 0);
  will-change: transform;
}

.page-rack-tooth {
  width: calc(var(--brand-w) * 0.13125);
  height: calc(var(--brand-w) * 0.08125);
  flex: 0 0 calc(var(--brand-w) * 0.08125);
  background: var(--orange);
  clip-path: polygon(0 0, 72% 0, 100% 20%, 100% 80%, 72% 100%, 0 100%);
}

.hero {
  position: relative;
  display: grid;
  align-items: center;
  background: linear-gradient(90deg, rgba(0, 0, 0, 0.96) 0%, rgba(0, 0, 0, 0.72) 55%, rgba(0, 0, 0, 0.3) 100%);
  padding-top: 160px;
  padding-bottom: 190px;
}

/* Oversized logo watermark behind the hero copy. A background-image can't take
   its own opacity, so it rides on a pseudo-element. The URL arrives as a custom
   property from the template because css-loader would try to resolve a
   root-relative url() here as a module. */
.hero::before {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background-image: var(--hero-watermark);
  background-repeat: no-repeat;
  background-position: 82% center;
  /* Deliberately taller than the hero so the mark is cropped by the top and
     bottom rules — it reads as continuing behind the nav and the next section. */
  background-size: auto 98%;
  opacity: 0.075;
}

@media (prefers-reduced-motion: reduce) {
  .page-rack-teeth {
    transform: none !important;
  }
}

/* Narrow screens: the hero is tall and thin, so a height-driven size would crop
   away the wrenches. Drive it from the width and let it bleed side to side. */
@media (max-width: 900px) {
  .hero::before {
    background-size: 132% auto;
  }
}

.hero h1 {
  font-size: clamp(2.8rem, 7vw, 6.1rem);
  font-weight: 200;
  line-height: 0.96;
  letter-spacing: 0.018em;
  text-transform: uppercase;
  max-width: 14ch;
}

/* The hero's logo watermark is an absolutely positioned ::before at z-index 0.
   In-flow content paints below positioned siblings, so without a lift of its
   own the copy would sit under the watermark once the blanket rule went. */
.hero-inner {
  position: relative;
  z-index: 1;
  display: grid;
  gap: clamp(2rem, 4vw, 3rem);
}

.demo-log-callout {
  width: min(72vw, 100%);
  justify-self: center;
  min-height: 68px;
  display: flex;
  justify-content: space-between;
  gap: 1.5rem;
  align-items: center;
  padding: 0.9rem clamp(1rem, 3vw, 1.6rem);
  border: 2px solid var(--orange);
  border-radius: var(--radius-sm);
  background: var(--white);
  color: var(--black);
  font-family: 'Jost', 'Barlow', sans-serif;
  font-size: clamp(1.15rem, 2.4vw, 1.75rem);
  font-weight: 600;
  letter-spacing: 0.025em;
  text-transform: uppercase;
  white-space: nowrap;
  transition: background-color 0.3s ease, color 0.3s ease, transform 0.35s ease;
}

.demo-log-callout > span:last-child {
  flex: 0 0 auto;
  font-size: 1.6em;
  font-weight: 400;
  line-height: 1;
}

.demo-log-callout:hover,
.demo-log-callout:focus-visible {
  background: var(--black);
  color: var(--orange);
  transform: translateY(-2px);
}

@media (max-width: 520px) {
  .demo-log-callout {
    width: 100%;
    gap: 0.5rem;
    padding-inline: 0.8rem;
    font-size: clamp(0.78rem, 3.2vw, 1rem);
  }
}

.hero-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(260px, 0.8fr);
  gap: clamp(2rem, 7vw, 6.5rem);
  align-items: start;
}

.hero-copy {
  margin: 1rem 0 1.4rem;
  max-width: 56ch;
  color: var(--white);
}

/* The old single margin of clamp(8rem, 12vw, 10.5rem) held the service links
   down below the demo callout. That gap is now split either side of the
   estimator button so it sits centred in the space rather than crowding
   against one end, and the hero keeps the height it had. */
.hero-side {
  display: grid;
}

/* Uneven on purpose. .hero-inner already puts its own grid gap between the
   callout and this column, so an equal margin either side of the button lands
   it low. The smaller top margin absorbs that gap and leaves the button
   centred in the space a reader actually sees. */
.hero-estimate {
  justify-self: center;
  margin-top: clamp(1.2rem, 2.6vw, 2.4rem);
  text-align: center;
}

.hero-service-links {
  display: grid;
  gap: 0.55rem;
  margin-top: clamp(3.4rem, 6vw, 5.4rem);
}

.hero-service-link {
  display: inline-flex;
  align-items: center;
  min-height: 42px;
  padding: 0.62rem 0.85rem;
  border: 1px solid rgba(255, 255, 255, 0.42);
  border-radius: 5px;
  background: rgba(0, 0, 0, 0.36);
  color: var(--white);
  font-size: 0.9rem;
  font-weight: 500;
  letter-spacing: 0.02em;
  transition: border-color 0.25s ease, background-color 0.25s ease, color 0.25s ease, transform 0.25s ease;
}

@media (max-width: 900px) {
  .hero-grid {
    grid-template-columns: 1fr;
    gap: 1.6rem;
  }

  /* Stacked into one column, so there is no gap left to centre anything in. */
  .hero-estimate {
    justify-self: stretch;
    margin-top: 0;
  }

  .hero-service-links {
    margin-top: 1.2rem;
    max-width: 560px;
  }
}

.hero-service-link:hover,
.hero-service-link:focus-visible {
  border-color: var(--orange);
  background: var(--orange);
  color: var(--black);
  transform: translateY(-2px);
}

.compact {
  margin-bottom: 0;
}

.section-title {
  font-size: var(--h2);
}

.service-grid {
  margin-top: 1.2rem;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1rem;
}

.service-card {
  padding: 1rem;
  color: inherit;
  text-decoration: none;
  background: rgba(0, 0, 0, 0.62);
  border-color: rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(18px);
  transition: transform 0.55s cubic-bezier(0.22, 1, 0.36, 1), border-color 0.55s ease;
}

.service-card:hover {
  transform: translateY(-7px);
  border-color: rgba(255, 90, 2, 0.6);
}

.service-card img {
  width: 88px;
  height: 88px;
  object-fit: cover;
  border-radius: 999px;
}

.service-card h3 {
  margin: 0.9rem 0 0.45rem;
  font-size: var(--h3);
  font-weight: 500;
}

.service-card p {
  color: var(--white);
  min-height: 84px;
}

.service-card a {
  color: var(--orange);
  font-weight: 700;
}

/* Sections below carry no fill of their own, matching the services section
   above. Anything opaque here paints over the fixed gear layer rather than
   sitting beside it: at the 0.72 this section used to carry, roughly a quarter
   of the atmosphere survived to be seen, which is what made this half of the
   page read as flat black while the identical machinery ran behind it. Cards
   get a fill because they hold text. Sections do not. */
.split-grid {
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  align-items: start;
  gap: clamp(1.6rem, 4vw, 3rem);
}

/* Set like the hero and the aperture rather than like body copy. At --h2 in
   sentence case this claim was making an argument in a quieter voice than the
   headings above it, which is most of why the lower half read as an older
   page bolted on beneath a newer one. */
.split-grid h2 {
  font-size: clamp(2rem, 4.4vw, 3.4rem);
  font-weight: 200;
  line-height: 1;
  letter-spacing: 0.018em;
  text-transform: uppercase;
  max-width: 16ch;
  margin-bottom: 1rem;
}

.split-grid p {
  color: var(--white);
  margin-bottom: 1.4rem;
  max-width: 62ch;
}

.split-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.7rem;
  align-items: center;
}

/* The copy here is long enough that a centred image would leave several hundred
   pixels of hole beside it. Pinning it below the sticky nav keeps the machine
   in view for the whole read instead, and the column stops being dead space. */
.why-image {
  position: sticky;
  top: 140px;
  border-radius: 30px;
  border: 1px solid rgba(255, 255, 255, 0.18);
}

/* An <hr> because this genuinely is a thematic break: everything above argues
   the CFM programme, everything below answers general service questions. The
   orange leading segment picks up the nav and footer rules so the break reads
   as part of the furniture rather than a stray line. */
.section-divider {
  height: 2px;
  margin: 0;
  border: 0;
  background: linear-gradient(90deg, var(--orange) 0 15%, rgba(255, 255, 255, 0.16) 15% 100%);
}

.faq-wrap {
  display: grid;
  grid-template-columns: 1.04fr 1fr;
  gap: 1.6rem;
  align-items: start;
}

.faq-content {
  display: grid;
  gap: 0.8rem;
  padding: 1.15rem;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 26px;
  background: rgba(0, 0, 0, 0.62);
  backdrop-filter: blur(18px);
}

.faq-content h2 {
  font-size: var(--h2);
}

.faq-lead {
  color: var(--white);
}

.faq-image {
  border-radius: 22px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  min-height: 450px;
  object-fit: cover;
}

/* Layout only, the same job .service-grid does above. The items are the cards;
   holding them inside a second filled and bordered panel stacked a third veil
   over the gears and gave this column the boxes-inside-boxes look that dated
   it. */
.accordion-wrap {
  display: grid;
  gap: 0.75rem;
}

.accordion-item {
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.62);
  backdrop-filter: blur(18px);
  overflow: hidden;
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

/* The open panel inverts to a solid orange fill with black type — the palette
   has no tints to fall back on, so state is carried by a full swap. */
.accordion-item.open {
  border-color: var(--orange);
  background: var(--orange);
}

.accordion-toggle {
  width: 100%;
  background: none;
  border: 0;
  color: var(--white);
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-align: left;
  gap: 0.9rem;
  padding: 0.9rem 1rem;
  cursor: pointer;
}

.accordion-title {
  margin: 0;
  font-size: 1.03rem;
  line-height: 1.35;
}

.accordion-icon {
  color: var(--white);
  min-width: 1.5rem;
  min-height: 1.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid var(--line);
  background: transparent;
  transition: transform 0.2s ease, border-color 0.2s ease;
}

.accordion-item.open .accordion-toggle,
.accordion-item.open .accordion-icon {
  color: var(--black);
  border-color: var(--black);
}

.accordion-item.open .accordion-toggle:focus-visible {
  outline-color: var(--black);
}

.accordion-icon.open {
  transform: rotate(180deg);
}

.accordion-list {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.25s ease;
}

.accordion-list.open {
  grid-template-rows: 1fr;
}

.accordion-list-content {
  min-height: 0;
  overflow: hidden;
  padding: 0 1rem 1rem;
  color: var(--black);
  line-height: 1.6;
}

@media (max-width: 1020px) {
  .service-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 760px) {
  .service-grid,
  .split-grid,
  .faq-wrap {
    grid-template-columns: 1fr;
  }

  .faq-image {
    min-height: 320px;
  }

  /* Stacked, so there is no second column for it to sit beside — pinning it
     here would park the truck over the copy for the length of the section. */
  .why-image {
    position: static;
  }
}

@media (max-width: 991px) {
  .hero {
    padding-top: 220px;
  }
}

@media (max-width: 767px) {
  .hero {
    padding-top: 170px;
    padding-bottom: 150px;
  }
}

@media (max-width: 479px) {
  .hero {
    padding-top: 150px;
    padding-bottom: 130px;
  }
}
</style>
